import React, { useEffect, useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Tag from '@amzn/meridian/tag';
import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import useGetFloorplan from 'src/store/hooks/useGetFloorplan';
import { FloorplanViewer, FloorplanProvider, PolygonLayer } from '@amzn/ihm-floorplan-viewer';
import { transformFPData } from '../zoneViewer/zoneMapDataHelper';
import { computeFixtureAllocations, FixtureSequenceEntry } from '../allocation/computeFixtureAllocations';
import { buildFixturePositionMap } from '../allocation/buildFixturePositionMap';
import { buildFixtureOrderingMap } from '../pickPath/buildFixtureOrderingMap';
import useDisplayModes from '../zoneViewer/displayModes';
import { buildPickPathFromOrdering } from '../pickPath/buildPickPathFromOrdering';
import { StoreCardProps } from './types';

const StoreCard: React.FC<StoreCardProps & { expanded?: boolean; onRemove?: (storeId: string) => void }> = ({
  storeId,
  date,
  department,
  sourceAllocations,
  onDataLoaded,
  onClick,
  publishStatus = 'pending',
  publishError,
  expanded = false,
  onRemove,
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const { floorplanList, isLoading: floorplanLoading } = useGetFloorplan({
    storeId,
    date,
  });

  const fixtures = useMemo(() => (floorplanList.length > 0 ? (floorplanList[0].fixtures ?? []) : []), [floorplanList]);

  // Filter fixtures to selected department only
  const departmentFixtures = useMemo(
    () => (department ? fixtures.filter((f) => f.department === department) : fixtures),
    [fixtures, department],
  );

  const fpData = useMemo(() => transformFPData(departmentFixtures), [departmentFixtures]);

  const fixtureOrderingMap = useMemo(() => buildFixtureOrderingMap(departmentFixtures), [departmentFixtures]);

  const readinessStatus = useMemo(() => {
    if (fixtureOrderingMap.size > 0) return 'ready' as const;
    return 'needs-sequence' as const;
  }, [fixtureOrderingMap]);

  const fixturePositionMap = useMemo(() => buildFixturePositionMap(departmentFixtures), [departmentFixtures]);

  const fixtureAssignments = useMemo(() => {
    if (readinessStatus !== 'ready') return undefined;
    const seq: FixtureSequenceEntry[] = Array.from(fixtureOrderingMap.entries()).map(([fixtureId, sequenceOrder]) => ({
      fixtureId,
      sequenceOrder,
    }));
    const assignments = computeFixtureAllocations(sourceAllocations, seq, fixturePositionMap);
    return assignments.length > 0 ? assignments : undefined;
  }, [readinessStatus, fixtureOrderingMap, sourceAllocations, fixturePositionMap]);

  // Build fixture → department map for display modes
  const fixtureDepartmentMap = useMemo(() => {
    const map = new Map<string, string>();
    fpData.forEach((f) => {
      if (f.id && f.department) map.set(f.id, f.department);
    });
    return map;
  }, [fpData]);

  const displayModes = useDisplayModes({}, fixtureAssignments, sourceAllocations, fixtureDepartmentMap, undefined);

  const displayMode = useMemo(() => {
    if (fixtureAssignments && fixtureAssignments.length > 0) {
      return displayModes.optionsList.find((m) => m.id === 'SUBCATEGORY_ALLOCATION') ?? displayModes.defaultOption;
    }
    return displayModes.defaultOption;
  }, [fixtureAssignments, displayModes]);

  // Build pick path overlay from fixture ordering
  const pickPathLayers = useMemo(
    () => buildPickPathFromOrdering(fixtureOrderingMap, fpData),
    [fixtureOrderingMap, fpData],
  );

  const pickPathOverlay =
    pickPathLayers.features.length > 0 ? (
      <PolygonLayer data={pickPathLayers} defaultStyle={{ showArrows: true, outlineStyle: 'solid' }} />
    ) : null;

  // Report loaded data to parent
  useEffect(() => {
    if (!floorplanLoading && floorplanList.length > 0) {
      onDataLoaded(storeId, {
        floorplanData: floorplanList,
        fixtureOrderingMap,
        readinessStatus,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorplanLoading, storeId, readinessStatus]);

  const isLoading = floorplanLoading;

  const statusBadge = () => {
    switch (publishStatus) {
      case 'published':
        return <Tag type="success">Published</Tag>;
      case 'failed':
        return <Tag type="error">Failed</Tag>;
      case 'network-error':
        return <Tag type="error">Network Error</Tag>;
      case 'publishing':
        return <Tag type="theme">Publishing...</Tag>;
      default:
        return null;
    }
  };

  return (
    <div
      onClick={() => {
        if (!showRemoveModal) onClick();
      }}
      role="button"
      aria-label={`Store ${storeId} - ${readinessStatus === 'ready' ? 'Ready' : 'Needs Sequence'}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !showRemoveModal) onClick();
      }}
      style={{ cursor: 'pointer' }}
    >
      <Column spacing="200" spacingInset="300" backgroundColor="alternateSecondary">
        <Row alignmentHorizontal="justify" alignmentVertical="center">
          <Text type="b300">{storeId}</Text>
          <Row spacing="200">
            {statusBadge()}
            <Tag type={readinessStatus === 'ready' ? 'success' : 'warning'}>
              {readinessStatus === 'ready' ? 'Ready' : 'Needs Sequence'}
            </Tag>
            {onRemove && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowRemoveModal(true);
                }}
                style={{ display: 'inline-flex', cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                aria-label={`Remove store ${storeId}`}
              >
                <Button size="small" type="icon">
                  ✕
                </Button>
              </div>
            )}
          </Row>
        </Row>

        {/* Remove confirmation modal */}
        <Modal title={`Remove ${storeId}?`} open={showRemoveModal} onClose={() => setShowRemoveModal(false)}>
          <Text>Are you sure you want to remove {storeId} from the target list? This cannot be undone.</Text>
          <ModalFooter>
            <Row spacing="300" alignmentHorizontal="end">
              <Button type="secondary" onClick={() => setShowRemoveModal(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  setShowRemoveModal(false);
                  if (onRemove) onRemove(storeId);
                }}
              >
                Remove
              </Button>
            </Row>
          </ModalFooter>
        </Modal>

        {publishStatus === 'failed' && publishError && (
          <Text type="b100" color="error">
            {publishError}
          </Text>
        )}

        {isLoading ? (
          <Column alignmentHorizontal="center" spacingInset="500">
            <Loader type="circular" size="small" />
          </Column>
        ) : (
          fpData.length > 0 &&
          displayMode && (
            <div
              style={{
                height: expanded ? 'calc(100vh - 120px)' : 200,
                width: '100%',
                position: 'relative',
              }}
              onClick={(e) => {
                if (expanded) e.stopPropagation();
              }}
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  textAlign: 'center',
                  padding: '4px 0',
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                role="button"
                tabIndex={0}
                aria-label={expanded ? 'Click to collapse' : 'Click to expand'}
              >
                {expanded ? '▲ Click to collapse' : '▼ Click to expand'}
              </div>
              <FloorplanProvider key={`${storeId}-${expanded}-${fixtureAssignments?.length ?? 0}`}>
                <FloorplanViewer fixtureData={fpData} displayMode={displayMode}>
                  {pickPathOverlay}
                </FloorplanViewer>
              </FloorplanProvider>
            </div>
          )
        )}
      </Column>
    </div>
  );
};

export default StoreCard;
